import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { CalendarDays, MapPin, Euro } from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then(r => setEv(r.data)).catch(() => setEv(null));
  }, [id]);

  if (!ev) return <div className="mx-auto max-w-7xl px-6 py-20 text-white/70">Caricamento...</div>;

  const reserve = async () => {
    if (!form.name || !form.email) return toast.error("Nome e email obbligatori");
    setBusy(true);
    try {
      await api.post(`/events/${id}/reserve`, { event_id: id, ...form });
      toast.success("Prenotazione confermata! Ti aspettiamo.");
      setForm({ name:"", email:"", phone:"" });
    } catch (e) { toast.error("Errore nella prenotazione"); }
    setBusy(false);
  };

  const buyTicket = async () => {
    if (!ev.lookup_key) return toast.error("Biglietto non disponibile");
    if (!form.name || !form.email) return toast.error("Nome e email obbligatori");
    setBusy(true);
    try {
      // Save reservation first so buyer contact is captured even if payment is abandoned
      const { data: res } = await api.post(`/events/${ev.id}/reserve`, { event_id: ev.id, ...form });
      const { data } = await api.post("/payments/checkout", {
        lookup_key: ev.lookup_key, quantity: 1,
        origin_url: window.location.origin,
        metadata: { event_id: ev.id, type: "event_ticket", reservation_id: res.id,
                    buyer_name: form.name, buyer_email: form.email, buyer_phone: form.phone }
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Errore checkout"); setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="aspect-square bg-black overflow-hidden border border-[#FFFFFF] flex items-center justify-center">
            {ev.poster_url ? <img src={ev.poster_url} alt={ev.title} className="w-full h-full object-contain"/> :
              <div className="text-white/30 font-display text-6xl">FLOW</div>}
          </div>
          {ev.gallery?.length > 0 && (
            <div className="mt-6">
              <div className="tag-pill mb-3">Galleria post-evento</div>
              <div className="grid grid-cols-3 gap-2">
                {ev.gallery.map((url, i) => (
                  <img key={i} src={url} alt="" className="aspect-square object-cover border border-[#FFFFFF]"/>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2">
          <div className="tag-pill mb-3">Evento</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase" data-testid="event-title">{ev.title}</h1>
          <div className="mt-4 space-y-2 text-white">
            <div className="flex items-center gap-2"><CalendarDays size={14} className="text-[#FFFFFF]"/>{new Date(ev.date).toLocaleString("it-IT")}</div>
            <div className="flex items-center gap-2"><MapPin size={14} className="text-[#FFFFFF]"/>{ev.location}</div>
            {ev.price_on_request ? (
              <div className="flex items-center gap-2 tag-pill w-fit">Prezzo su richiesta</div>
            ) : !ev.free ? (
              <div className="flex items-center gap-2"><Euro size={14} className="text-[#FFFFFF]"/>{ev.price.toFixed(2)}</div>
            ) : null}
          </div>
          <p className="mt-6 text-white whitespace-pre-line">{ev.description}</p>

          <div className="mt-8 card p-6">
            <h3 className="font-display text-2xl uppercase mb-4">
              {ev.price_on_request ? "Richiedi informazioni" : ev.free ? "Prenota il tuo posto" : "Acquista il biglietto"}
            </h3>
            <div className="grid gap-3">
              <input data-testid="event-name" placeholder="Nome e cognome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded"/>
              <input data-testid="event-email" placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded"/>
              <input data-testid="event-phone" placeholder="Telefono" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded"/>
              {ev.price_on_request ? (
                <a href={`/contatti?subject=${encodeURIComponent("Richiesta prezzo evento: " + ev.title)}`}
                  className="brutal-btn brutal-btn-primary" data-testid="event-request">
                  Contattaci per il prezzo
                </a>
              ) : ev.free ? (
                <button data-testid="event-reserve" onClick={reserve} disabled={busy} className="brutal-btn brutal-btn-primary">
                  {busy ? "Attendere..." : "Prenota gratis"}
                </button>
              ) : (
                <button data-testid="event-buy" onClick={buyTicket} disabled={busy} className="brutal-btn brutal-btn-primary">
                  {busy ? "Attendere..." : `Acquista €${ev.price.toFixed(2)}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
